import React, { useState, useEffect, useRef } from 'react';
import { useItems, useStockTransactions } from '../context/DataContext';
import './SubtractStock.css';

function SubtractStock() {
  const { data: items, loading: itemsLoading } = useItems();
  const { add: addStockTransaction, loading: txLoading } = useStockTransactions();
  
  // Autocomplete states
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const [formData, setFormData] = useState({
    itemId: '',
    itemCode: '',
    itemName: '',
    size: '',
    weightPerPcs: '',
    numberOfPackets: 1,
    totalWeight: '',
  });
  const [currentStock, setCurrentStock] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 0;
    return Math.round(value * 100) / 100;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter items based on search term
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
    setError('');
    setSuccess('');
    
    if (value.trim() === '') {
      setFilteredItems([]);
      setSelectedItem(null);
      setCurrentStock(null);
      setFormData({
        itemId: '',
        itemCode: '',
        itemName: '',
        size: '',
        weightPerPcs: '',
        numberOfPackets: 1,
        totalWeight: '',
      });
      return;
    }

    const filtered = items.filter(item => {
      const searchLower = value.toLowerCase();
      return (
        (item.itemCode || '').toLowerCase().includes(searchLower) ||
        (item.itemName || '').toLowerCase().includes(searchLower) ||
        (item.size || '').toLowerCase().includes(searchLower)
      );
    });
    setFilteredItems(filtered);
  };

  // Select an item from dropdown
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setSearchTerm(`${item.itemCode || ''} - ${item.itemName || ''} (${item.size || ''})`);
    setShowDropdown(false);
    setFilteredItems([]);
    
    // Round the weight per piece and stock values
    const roundedWeightPerPcs = roundToTwoDecimals(item.weightPerPcs || 0);
    const roundedTotalWeight = roundToTwoDecimals(item.totalWeight || 0);
    const roundedPackets = item.numberOfPackets || 0;
    
    setFormData({
      itemId: item.id,
      itemCode: item.itemCode || '',
      itemName: item.itemName || '',
      size: item.size || '',
      weightPerPcs: roundedWeightPerPcs,
      numberOfPackets: 1,
      totalWeight: '', // Empty, user will enter manually
    });
    
    setCurrentStock({
      totalWeight: roundedTotalWeight,
      numberOfPackets: roundedPackets,
    });
    setError('');
    setSuccess('');
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value,
    });
    setError('');
    setSuccess('');
  };

  // Highlight matching text in dropdown
  const highlightMatch = (text, search) => {
    const textStr = String(text || '');
    const searchStr = String(search || '');
    
    if (!searchStr.trim() || !textStr) return textStr;
    
    try {
      const parts = textStr.split(new RegExp(`(${searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
      return parts.map((part, i) => 
        part.toLowerCase() === searchStr.toLowerCase() ? 
          <span key={i} className="highlight">{part}</span> : part
      );
    } catch (error) {
      console.error('Error in highlightMatch:', error);
      return textStr;
    }
  };

  // Validate and handle subtraction
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!selectedItem) {
        setError('Please select an item from the search');
        setLoading(false);
        return;
      }
      
      const packets = parseInt(formData.numberOfPackets);
      if (!packets || packets <= 0) {
        setError('Number of packets must be greater than 0');
        setLoading(false);
        return;
      }

      const totalWeight = parseFloat(formData.totalWeight);
      if (!totalWeight || totalWeight <= 0) {
        setError('Total weight must be greater than 0');
        setLoading(false);
        return;
      }

      // Round the input values for comparison
      const roundedTotalWeight = roundToTwoDecimals(totalWeight);
      const roundedCurrentWeight = roundToTwoDecimals(currentStock?.totalWeight || 0);
      const roundedCurrentPackets = currentStock?.numberOfPackets || 0;

      // Validate available stock with rounded values
      if (packets > roundedCurrentPackets) {
        setError(`❌ Insufficient packets. Available: ${roundedCurrentPackets}`);
        setLoading(false);
        return;
      }

      if (roundedTotalWeight > roundedCurrentWeight) {
        setError(`❌ Insufficient weight. Available: ${roundedCurrentWeight.toFixed(2)} g`);
        setLoading(false);
        return;
      }

      // Calculate the new stock after subtraction
      const newTotalWeight = roundToTwoDecimals(roundedCurrentWeight - roundedTotalWeight);
      const newPackets = roundedCurrentPackets - packets;

      // Subtract stock using DataContext
      const result = await addStockTransaction({
        itemCode: formData.itemCode,
        itemName: formData.itemName,
        size: formData.size,
        operation: 'OUT',
        totalWeight: roundedTotalWeight,
        numberOfPackets: packets,
      });

      if (result.success) {
        setSuccess(`✅ Successfully subtracted ${packets} packet(s) (${roundedTotalWeight.toFixed(2)} g) from ${formData.itemName}!`);
        
        // Update current stock display with rounded values
        setCurrentStock({
          totalWeight: newTotalWeight,
          numberOfPackets: newPackets,
        });

        // Reset packet and weight fields but keep item selected
        setFormData(prev => ({
          ...prev,
          numberOfPackets: 1,
          totalWeight: '',
        }));

        // Update the selected item in the search bar to show updated stock
        if (selectedItem) {
          const updatedItem = items.find(item => item.id === selectedItem.id);
          if (updatedItem) {
            setSearchTerm(`${updatedItem.itemCode || ''} - ${updatedItem.itemName || ''} (${updatedItem.size || ''})`);
          }
        }
      } else {
        setError('❌ ' + (result.message || 'Failed to subtract stock'));
      }
    } catch (err) {
      setError('❌ ' + (err.message || 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const maxPackets = currentStock?.numberOfPackets || 0;
  const maxWeight = currentStock?.totalWeight || 0;
  const isLoading = loading || itemsLoading || txLoading;

  // Check if stock is low
  const isLowStock = maxPackets > 0 && maxPackets < 5;

  if (itemsLoading) {
    return <div className="loading">Loading items...</div>;
  }

  return (
    <div className="subtract-stock-page">
      <h1 className="page-title">Subtract Stock</h1>
      <p className="page-description">Remove stock from inventory</p>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="subtract-container">
        <form onSubmit={handleSubmit} className="subtract-form">
          <div className="form-card">
            <div className="form-group">
              <label>Search Item *</label>
              <div className="search-container" ref={searchRef}>
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search by Item Code, Name, or Size..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                  autoComplete="off"
                  required
                />
                {showDropdown && filteredItems.length > 0 && (
                  <div className="search-dropdown" ref={dropdownRef}>
                    {filteredItems.map(item => {
                      const itemPackets = item.numberOfPackets || 0;
                      const isOutOfStock = itemPackets === 0;
                      return (
                        <div
                          key={item.id}
                          className={`search-item ${isOutOfStock ? 'out-of-stock' : ''}`}
                          onClick={() => !isOutOfStock && handleSelectItem(item)}
                          style={isOutOfStock ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                        >
                          <div className="search-item-main">
                            <span className="item-code">
                              {highlightMatch(item.itemCode, searchTerm)}
                            </span>
                            <span className="item-name">
                              {highlightMatch(item.itemName, searchTerm)}
                            </span>
                            <span className="item-size">
                              Size: {highlightMatch(item.size, searchTerm)}
                            </span>
                          </div>
                          <div className="search-item-details">
                            <span className="item-stock">
                              📦 {itemPackets} packets
                              {isOutOfStock && ' (Out of Stock)'}
                            </span>
                            <span className="item-weight">
                              ⚖️ {roundToTwoDecimals(item.weightPerPcs || 0).toFixed(2)} g/pcs
                            </span>
                            <span className="item-total">
                              📊 {roundToTwoDecimals(item.totalWeight || 0).toFixed(2)} g
                            </span>
                            {isOutOfStock && (
                              <span className="out-of-stock-badge">🚫 Out of Stock</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {showDropdown && searchTerm && filteredItems.length === 0 && (
                  <div className="search-dropdown no-results">
                    <div className="no-results-message">
                      No items found matching "{searchTerm}"
                    </div>
                  </div>
                )}
              </div>
              <small className="help-text">
                {selectedItem ? '✅ Item selected. Enter quantity to subtract.' : 'Type to search and select an item'}
              </small>
            </div>

            {selectedItem && (
              <>
                <div className="selected-item-info">
                  <h3>📋 Selected Item Details</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">Item Code:</span>
                      <span className="value">{selectedItem.itemCode || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Item Name:</span>
                      <span className="value">{selectedItem.itemName || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Size:</span>
                      <span className="value">{selectedItem.size || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Weight/Pcs:</span>
                      <span className="value">{roundToTwoDecimals(selectedItem.weightPerPcs || 0).toFixed(2)} g</span>
                    </div>
                  </div>
                </div>

                <div className={`current-stock-info ${isLowStock ? 'low-stock' : ''}`}>
                  <h4>📦 Current Stock</h4>
                  <div className="stock-details">
                    <div className="stock-detail">
                      <span className="label">Total Weight:</span>
                      <span className="value">{maxWeight.toFixed(2)} g</span>
                    </div>
                    <div className="stock-detail">
                      <span className="label">Packets:</span>
                      <span className="value">
                        {maxPackets}
                        {isLowStock && maxPackets > 0 && (
                          <span className="low-stock-warning"> ⚠️ Low Stock!</span>
                        )}
                        {maxPackets === 0 && (
                          <span className="out-of-stock-warning"> 🚫 Out of Stock</span>
                        )}
                      </span>
                    </div>
                  </div>
                  {isLowStock && maxPackets > 0 && (
                    <div className="low-stock-message">
                      ⚠️ Only {maxPackets} packet(s) remaining. Consider restocking soon.
                    </div>
                  )}
                  {maxPackets === 0 && (
                    <div className="out-of-stock-message">
                      🚫 This item is currently out of stock.
                    </div>
                  )}
                </div>

                {maxPackets > 0 && (
                  <div className="subtract-inputs">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Number of Packets *</label>
                        <input
                          type="number"
                          name="numberOfPackets"
                          className="form-control"
                          value={formData.numberOfPackets}
                          onChange={handleChange}
                          min="1"
                          max={maxPackets}
                          required
                        />
                        <small className="help-text">
                          Max: {maxPackets} packet{maxPackets !== 1 ? 's' : ''}
                        </small>
                      </div>

                      <div className="form-group">
                        <label>Total Weight (g) *</label>
                        <input
                          type="number"
                          name="totalWeight"
                          className="form-control"
                          value={formData.totalWeight}
                          onChange={handleChange}
                          step="0.01"
                          min="0.01"
                          max={maxWeight}
                          required
                        />
                        <small className="help-text">
                          Max: {maxWeight.toFixed(2)} g
                        </small>
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      className="btn btn-danger btn-block" 
                      disabled={isLoading || maxPackets === 0}
                    >
                      {isLoading ? 'Processing...' : 'Subtract Stock'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </form>

        <div className="subtract-info">
          <h3>📋 Important Notes</h3>
          <ul>
            <li>Search for items by Code, Name, or Size</li>
            <li>Weight per piece is displayed for reference only</li>
            <li>Enter number of packets and total weight manually</li>
            <li>The system validates available stock before subtraction</li>
            <li>Out of stock items are disabled and shown in grey</li>
            <li>Low stock warning appears when packets {'<'} 5</li>
            <li>All subtractions are recorded in history</li>
            <li>All weights are automatically rounded to 2 decimal places</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SubtractStock;